import { useRef } from "react";
import type { ProfileData } from "hooks/useProfile";
import Input from "Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";

const ROLE_LABELS: Record<string, string> = {
  admin: "CSC Admin",
  gc: "General Contractor",
  lender: "Bank / Lender",
  owner: "Owner",
  inspector: "Inspector",
  sub: "Subcontractor",
};

interface Props {
  profile: ProfileData;
  onUploadAvatar: (file: File) => Promise<boolean>;
  uploadingAvatar?: boolean;
}

export default function ProfileHeader({
  profile,
  onUploadAvatar,
  uploadingAvatar = false,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initials =
    `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase() ||
    "?";
  const fullName =
    [profile.firstName, profile.lastName].filter(Boolean).join(" ") || "User";
  const roleLabel = ROLE_LABELS[profile.role] ?? profile.role;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    void onUploadAvatar(file);
    e.target.value = "";
  }

  return (
    <div className="pf-header">
      <div className="pf-header__avatar">
        {profile.avatar ? (
          <img
            src={profile.avatar}
            alt={fullName}
            className="pf-header__avatar-img"
          />
        ) : (
          initials
        )}
        {uploadingAvatar && <div className="pf-header__avatar-uploading" />}
        <Input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="pf-header__avatar-input"
          onChange={handleFileChange}
        />
        <Button
          type="button"
          className="pf-header__avatar-edit"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingAvatar}
          title="Change photo"
        >
          📷
        </Button>
      </div>
      <div>
        <h1>{fullName}</h1>
        <div className="pf-header__meta">
          {profile.organization}
          {profile.address ? ` · ${profile.address}` : ""}
        </div>
        <div className="pf-header__role-badge">⚙ {roleLabel}</div>
      </div>
    </div>
  );
}
